const GET_CUSTOMER_PROPERTIES = '/property/get-customer-properties';
const GET_CUSTOMER_PROPERTIES_FROM = '/property/get-property-fields';
const SAVE_PROPERTY_FIELD = '/property/save-property-fields';
const GET_CUSTOMER_PROPERTY = '/property/get-customer-property-fields';
const CUSTOMER_PROPERTIES_CHANGED_EVENT = 'octopus:customer-properties-changed';
const CUSTOMER_PROPERTIES_CHANGED_STORAGE_KEY = 'octopus:customer-properties-changed';

function buildFormData(formData, data, parentKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
        Object.keys(data).forEach(key => {
            buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
        });
    } else {
        const value = data == null ? '' : data;
        formData.append(parentKey, value);
    }
}

function toPositiveNumber(value) {
    const numberValue = Number(value || 0);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
}

function notifyCustomerPropertiesChanged(data, response) {
    if (typeof window === 'undefined') {
        return;
    }

    const responseData = response && response.data && typeof response.data === 'object'
        ? response.data
        : {};
    const detail = {
        customerId: toPositiveNumber(data && data.customer_id),
        propertyId: toPositiveNumber(data && data.property_id),
        customerPropertyId: toPositiveNumber(
            responseData.customer_property_id || (data && data.customer_property_id)
        ),
        changedAt: Date.now() + Math.random()
    };

    // The legacy Vue customer-property flow shares the page with Nuxt routes.
    // Invalidate the same global cache even when no CustomerProperties component
    // is currently mounted, so navigating elsewhere cannot reuse stale values.
    if (window.__customerPropertiesResponseCache instanceof Map) {
        window.__customerPropertiesResponseCache.clear();
    }
    if (window.__customerPropertiesPendingRequests instanceof Map) {
        window.__customerPropertiesPendingRequests.clear();
    }
    window.__customerPropertiesInvalidationId = detail.changedAt;

    window.dispatchEvent(new CustomEvent(CUSTOMER_PROPERTIES_CHANGED_EVENT, { detail }));

    try {
        window.localStorage.setItem(
            CUSTOMER_PROPERTIES_CHANGED_STORAGE_KEY,
            JSON.stringify(detail)
        );
    } catch (error) {
        // Ignore storage restrictions; the same-tab CustomEvent still refreshes Nuxt consumers.
    }
}

function toFieldArray(data) {
    if (Array.isArray(data)) {
        return data.filter(Boolean);
    }

    if (data && typeof data === 'object') {
        return Object.values(data).filter(Boolean);
    }

    return [];
}

function savedOptionIds(rows) {
    const ids = new Set();

    rows.forEach(row => {
        const directId = Number(row && row.property_field_value_id);
        if (Number.isFinite(directId) && directId > 0) {
            ids.add(String(directId));
        }

        const value = row && row.value;
        if (typeof value === 'string') {
            value.split(',').map(item => item.trim()).filter(Boolean).forEach(item => {
                if (/^\d+$/.test(item)) {
                    ids.add(item);
                }
            });
        } else if (typeof value === 'number' && value > 0) {
            ids.add(String(value));
        }
    });

    return ids;
}

/**
 * Edit popups must use the same field definition as Add. The saved-value endpoint
 * can contain historical rows for removed/core fields and duplicate rows for
 * checkbox values, so it is not safe to use that response as the form schema.
 *
 * The configured property fields own membership, order, labels, field types and
 * options. Saved rows only supply values/row ids for the matching field id.
 */
function mergeConfiguredPropertyFields(configuredData, savedData) {
    const configuredFields = toFieldArray(configuredData);
    const savedFields = toFieldArray(savedData);
    const savedByFieldId = new Map();

    savedFields.forEach(field => {
        const id = String(field && field.property_field_id || '');
        if (!id) {
            return;
        }

        const rows = savedByFieldId.get(id) || [];
        rows.push(field);
        savedByFieldId.set(id, rows);
    });

    const seenFieldIds = new Set();
    const merged = [];

    configuredFields.forEach(configuredField => {
        const id = String(configuredField && configuredField.property_field_id || '');
        if (!id || seenFieldIds.has(id)) {
            return;
        }
        seenFieldIds.add(id);

        const rows = savedByFieldId.get(id) || [];
        const savedField = rows[0] || null;
        const field = {...configuredField};

        if (savedField) {
            [
                'customer_property_field_value_id',
                'customer_property_id',
                'customer_id',
                'property_field_value_id',
                'value_extra_details'
            ].forEach(key => {
                if (typeof savedField[key] !== 'undefined') {
                    field[key] = savedField[key];
                }
            });
        }

        if (Number(field.type) === 5) {
            const selectedIds = savedOptionIds(rows);
            field.options = toFieldArray(field.options).map(option => ({
                ...option,
                selected: selectedIds.has(String(option.property_field_value_id))
            }));
            field.value = Array.from(selectedIds).join(',');
        } else if (Number(field.type) === 4) {
            const selectedId = savedField && (
                savedField.property_field_value_id || savedField.value
            );
            field.property_field_value_id = selectedId || 0;
            field.value = selectedId || '';
        } else {
            field.value = savedField && savedField.value != null ? savedField.value : '';
        }

        merged.push(field);
    });

    return merged.sort((first, second) => Number(first.order || 0) - Number(second.order || 0));
}

module.exports = {
    getCustomerProperties: function getCustomerProperties(customerId, tradingNameId,id,type) {
        return window.axios.get(GET_CUSTOMER_PROPERTIES, {
            params: {
                customer_id: customerId,
                trading_id: tradingNameId,
                id: id,
                type: type
            }
        });
    },
    getCustomerProperty: function getCustomerProperty(customerPropertyId, propertyId, custom) {
        const savedPropertyRequest = window.axios.get(GET_CUSTOMER_PROPERTY, {
            params: {
                customer_property_id: customerPropertyId,
                property_id: propertyId,
                custom:custom
            }
        });
        const configuredFieldsRequest = window.axios.get(GET_CUSTOMER_PROPERTIES_FROM, {
            params: {
                id: propertyId
            }
        }).catch(() => ({data: []}));

        return Promise.all([configuredFieldsRequest, savedPropertyRequest])
            .then(([configuredResponse, savedResponse]) => {
                const configuredFields = toFieldArray(configuredResponse.data);
                if (!configuredFields.length) {
                    return savedResponse;
                }

                return {
                    ...savedResponse,
                    data: mergeConfiguredPropertyFields(configuredFields, savedResponse.data)
                };
            });
    },
    getCustomerPropertiesForm: function getCustomerPropertiesForm(propertyId) {
        return window.axios.get(GET_CUSTOMER_PROPERTIES_FROM, {
            params: {
                id: propertyId
            }
        });
    },
    updateCustomerProperty: function updateCustomerProperty(data){
        let form = new FormData();

        buildFormData(form, data);

        return window.axios.post(SAVE_PROPERTY_FIELD, form, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;Content-Type:multipart/form-data; charset=UTF-8'
            }
        }).then(response => {
            notifyCustomerPropertiesChanged(data, response);
            return response;
        });
    },
    storeCustomerPropertyForCustomer: function storeCustomerPropertyForCustomer(data) {

        let form = new FormData();

        buildFormData(form, data);
        return window.axios.post(SAVE_PROPERTY_FIELD, form, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(response => {
            notifyCustomerPropertiesChanged(data, response);
            return response;
        });
    }
};  